<template>
  <div
    ref="rootRef"
    @focus.capture="onFocus"
    @blur.capture="onBlur"
  >
    <DSPicker
      ref="pickerRef"
      v-model="isOpen"
      :class="DS_ICON_PICKER_LIST_CLASSES.Root"
      data-cy="ds-icon-picker-list"
      :content-class="DS_ICON_PICKER_LIST_CLASSES.Panel"
      content-data-cy="ds-icon-picker-list__panel"
    >
      <template #activator="{ setRef, toggle }">
        <DSFormField
          :id="id"
          :label="props.label"
          :inline-edit="props.inlineEdit"
          :required="props.required"
          :disabled="props.disabled"
          :error-messages="dsFormField.errorMessages.value"
          :name="props.name"
          :tooltip="props.tooltip"
          :hidden-label="props.hiddenLabel"
        >
          <div
            :ref="el => setRef(el as HTMLElement)"
            :class="DS_ICON_PICKER_CLASSES.Root"
            data-cy="ds-icon-picker"
          >
            <DSBaseInput
              ref="baseInputRef"
              v-model="modelValue"
              v-bind="baseInputProps"
              @click="onInputClick(toggle)"
              @suffix:click="onInputClick(toggle)"
              @clear="onSelectIcon(null)"
            />
          </div>
        </DSFormField>
      </template>

      <div ref="contentRef">
        <DSIconPickerList
          :model-value="modelValue"
          :searchable="props.searchable"
          :disabled="props.disabled"
          @update:model-value="onSelectIcon"
        />
      </div>
    </DSPicker>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import DSIconPickerList from "@/src/design_system/form_elements/icon_picker/components/DSIconPickerList.vue";
import type { DSBaseInputProps } from "@/src/design_system/form_elements/base_input/types/dsBaseInput";
import { DS_BASE_INPUT_TYPE } from "@/src/design_system/form_elements/base_input/types/inputType";
import type { DSIconName } from "@/src/design_system/primitives/icon/types/iconName.generated";
import {
  DS_ICON_PICKER_CLASSES,
  DS_ICON_PICKER_LIST_CLASSES,
} from "@/src/design_system/form_elements/icon_picker/constants/classes";
import type { DSIconPickerProps } from "@/src/design_system/form_elements/icon_picker/types/props";
import type { DSIconPickerEmits } from "@/src/design_system/form_elements/icon_picker/types/emits";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { getExpandIcon } from "@/src/design_system/shared/utils/expand";
import DSPicker from "@/src/design_system/shared_modules/picker/components/DSPicker.vue";

defineOptions({ inheritAttrs: false });

const emit = defineEmits<DSIconPickerEmits>();

const props = withDefaults(defineProps<DSIconPickerProps>(), {
  hiddenLabel: false,
  inlineEdit: false,
  label: undefined,
  name: undefined,
  placeholder: "",
  required: false,
  disabled: false,
  errorMessages: undefined,
  rules: undefined,
  tooltip: undefined,
  searchable: true,
  clearable: true,
});

const modelValue = defineModel<DSIconName | null>({ default: null });

const isOpen = ref(false);
const pickerRef = ref<InstanceType<typeof DSPicker>>();
const rootRef = ref<HTMLElement>();
const contentRef = ref<HTMLElement>();

const dsFormField = useDSFormField<DSIconName | null>({
  name: computed(() => props.name),
  errorMessages: computed(() => props.errorMessages),
  value: modelValue,
  rules: computed(() => props.rules),
});

const hasError = computed(() => dsFormField.errorMessages.value.length > 0);

const baseInputProps = computed<DSBaseInputProps>(() => ({
  type: DS_BASE_INPUT_TYPE.Text,
  name: props.name,
  placeholder: modelValue.value ? undefined : props.placeholder,
  inlineEdit: props.inlineEdit,
  disabled: props.disabled,
  readonly: true,
  required: props.required,
  hasError: hasError.value,
  prefix: modelValue.value ?? undefined,
  suffix: getExpandIcon(isOpen.value),
  ariaExpanded: isOpen.value,
  autocomplete: "off",
  clearable: props.clearable,
  hiddenInput: !props.inlineEdit || !!modelValue.value,
  cursorPointer: true,
}));

const onInputClick = (toggle: () => void): void => {
  if (props.disabled) {
    return;
  }

  toggle();
};

const onSelectIcon = (icon: DSIconName | null) => {
  modelValue.value = icon;
  dsFormField.clearFieldErrors();
  pickerRef.value?.close();
};

const { id } = useDSFormFieldId({ props });

const baseInputRef = ref<InstanceType<typeof DSBaseInput>>();
const { focus } = useDSInput(baseInputRef);
const blur = () => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && rootRef.value?.contains(activeElement)) activeElement.blur();
};
const onFocus = (event: FocusEvent) => {
  if (event.relatedTarget instanceof Node && rootRef.value?.contains(event.relatedTarget)) return;
  emit("focus", event);
};
const onBlur = (event: FocusEvent) => {
  if (event.relatedTarget instanceof Node && rootRef.value?.contains(event.relatedTarget)) return;
  emit("blur", event);
  isOpen.value = false;
};

watch(isOpen, open => {
  if (!open && contentRef.value?.contains(document.activeElement)) focus();
});

defineExpose({ focus, blur });
</script>
